// Parse a prep-pack markdown document (output of interview_prep) into a flat
// list of practice questions, categorized.
//
// The interview-backend prompt produces this structure:
//
//   ## Process Overview
//   ...
//   ## Likely Questions — Technical
//   1. ...question...
//   2. ...question...
//   ## Likely Questions — Behavioral
//   ...
//   ## Likely Questions — Role-Specific
//   ...
//   ## Likely Questions — Background Red Flags
//   ...
//   ## Story Bank Mapping
//   | Question | Best Story | Fit |
//   |---|---|---|
//   | ... | ... | ... |
//
// Models occasionally drift: missing the em-dash, calling it "Technical
// Questions", or inverting the order. We tolerate that.

export type PrepCategory = 'technical' | 'behavioral' | 'role-specific' | 'red-flag';

export interface PrepQuestion {
  text: string;
  category: PrepCategory;
  storyHint?: string; // joined from "Story Bank Mapping" if a row matches
}

interface SectionMatch {
  category: PrepCategory;
  body: string;
}

// Map heading suffix words → category. Used both for strict em-dash form and
// the looser "Technical Questions" / "Questions: Behavioral" patterns.
const CATEGORY_KEYWORDS: { kw: RegExp; cat: PrepCategory }[] = [
  { kw: /\b(technical|coding|engineering|system\s*design)\b/i, cat: 'technical' },
  { kw: /\b(behavioral|behavioural|leadership|culture)\b/i, cat: 'behavioral' },
  { kw: /\b(role[-\s]*specific|jd[-\s]*specific|domain[-\s]*specific)\b/i, cat: 'role-specific' },
  { kw: /\b(red[-\s]*flag|gap|background|risk)\b/i, cat: 'red-flag' },
];

function classifyHeading(heading: string): PrepCategory | null {
  for (const { kw, cat } of CATEGORY_KEYWORDS) {
    if (kw.test(heading)) return cat;
  }
  return null;
}

// Strip leading bullet/number markers, leading + trailing category tags like
// "[technical]" / "[behavioral]" / "[inferred from JD]", and bold wrappers.
// Italics (`*...*`) are intentionally LEFT IN — the question is rendered via
// ReactMarkdown so the user sees them styled, not as literal asterisks.
function cleanQuestionLine(line: string): string {
  let s = line.trim();
  // Numbering / bullet
  s = s.replace(/^(?:[-*•]|\d+[.)])\s+/, '');
  // LEADING category tag: "[technical] *Question…*" (model often emits this)
  // Loop because a model might double-stack like "[technical] [inferred] Q"
  for (let i = 0; i < 3; i++) {
    const before = s;
    s = s.replace(/^\s*\[[^\]]+\]\s*/, '');
    if (s === before) break;
  }
  // TRAILING category tag: "Question? [technical]"
  s = s.replace(/\s*\[[^\]]+\]\s*$/g, '');
  // Bold wrapper "**Question**" — strip outer pair only
  s = s.replace(/^\*\*\s*|\s*\*\*$/g, '').trim();
  return s;
}

// Pull out lines that look like questions from a section body. Accepts
// numbered or bulleted lists; ignores blank lines and meta lines like
// "(based on role)". Heuristic: must contain a question mark OR start with
// "Tell me", "Walk me", "Describe", "What", "Why", "How", "Give an example".
const QUESTION_LEAD = /^(tell\s+me|walk\s+me|describe|what|why|how|when|give|explain|share)\b/i;

function extractQuestions(body: string, category: PrepCategory): PrepQuestion[] {
  const lines = body.split('\n');
  const out: PrepQuestion[] = [];
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    // Skip table rows (handled by parseStoryBank)
    if (line.startsWith('|')) continue;
    // Bullet/number prefix → must start a question line
    const isListItem = /^(?:[-*•]|\d+[.)])\s+/.test(line);
    if (!isListItem) continue;
    const cleaned = cleanQuestionLine(line);
    if (!cleaned) continue;
    if (cleaned.length < 8) continue; // skip "n/a" / one-word noise
    // Accept if it has a ? or starts with a question lead, otherwise skip
    // (avoids false-positive bullets like "  - based on role")
    if (!cleaned.includes('?') && !QUESTION_LEAD.test(cleaned)) continue;
    out.push({ text: cleaned, category });
  }
  return out;
}

// Find all H2 headings and the body under each up to the next H2. Tolerates
// non-em-dash separators ("--", "-", ":", or none).
function findSections(md: string): SectionMatch[] {
  const lines = md.split('\n');
  const sections: SectionMatch[] = [];
  let currentHeading: string | null = null;
  let currentBody: string[] = [];

  const flush = () => {
    if (currentHeading == null) return;
    const cat = classifyHeading(currentHeading);
    if (cat) sections.push({ category: cat, body: currentBody.join('\n') });
  };

  for (const line of lines) {
    const m = line.match(/^##\s+(.+?)\s*$/);
    if (m) {
      flush();
      currentHeading = m[1];
      currentBody = [];
      continue;
    }
    if (currentHeading != null) currentBody.push(line);
  }
  flush();
  return sections;
}

// Parse "## Story Bank Mapping" markdown table → { question → "Best Story · Fit" }
function parseStoryBank(md: string): Map<string, string> {
  const out = new Map<string, string>();
  const m = md.match(/##\s+Story\s+Bank\s+Mapping[^\n]*\n([\s\S]*?)(?=\n##\s|$)/i);
  if (!m) return out;
  const tableBody = m[1];
  const rows = tableBody.split('\n').filter((l) => l.trim().startsWith('|'));
  for (const row of rows) {
    // Skip header / separator rows
    if (/^\s*\|\s*-+/.test(row)) continue;
    const cells = row.split('|').map((c) => c.trim()).filter((_, i, arr) => i > 0 && i < arr.length - 1);
    if (cells.length < 2) continue;
    const question = cells[0];
    if (!question || /^question$/i.test(question)) continue; // header row
    const story = cells[1] || '';
    const fit = cells[2] || '';
    if (!story) continue;
    const hint = fit ? `${story} · Fit ${fit}` : story;
    out.set(question.toLowerCase(), hint);
  }
  return out;
}

export function parsePrepPack(md: string): PrepQuestion[] {
  if (!md || typeof md !== 'string') return [];
  const sections = findSections(md);
  const storyBank = parseStoryBank(md);
  const questions: PrepQuestion[] = [];
  for (const sec of sections) {
    questions.push(...extractQuestions(sec.body, sec.category));
  }
  // Attach story hints by fuzzy match (lowercase substring)
  for (const q of questions) {
    if (q.storyHint) continue;
    const key = q.text.toLowerCase();
    for (const [bankKey, hint] of storyBank.entries()) {
      // Either question contains bank key or bank key contains question (allow truncation either side)
      const a = key.length < bankKey.length ? key : bankKey;
      const b = key.length < bankKey.length ? bankKey : key;
      if (b.includes(a.slice(0, Math.min(40, a.length)))) {
        q.storyHint = hint;
        break;
      }
    }
  }
  // Dedup by text (model occasionally repeats a question across sections)
  const seen = new Set<string>();
  return questions.filter((q) => {
    const k = q.text.trim().toLowerCase();
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

export function countByCategory(qs: PrepQuestion[]): Record<PrepCategory, number> {
  const out: Record<PrepCategory, number> = {
    technical: 0, behavioral: 0, 'role-specific': 0, 'red-flag': 0,
  };
  for (const q of qs) out[q.category]++;
  return out;
}
